import { useLang, buildWhatsAppUrl } from "../i18n";
import { ArrowRight, MessageCircle, MapPin } from "lucide-react";

const HERO_BG =
  "https://customer-assets.emergentagent.com/job_1ed3182d-c2e3-4132-9c29-53360c74ba18/artifacts/k6tn9my2_386af907-08c1-421b-b1d5-abaa978d8193.jpeg";

export default function Hero() {
  const { t } = useLang();

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Black and grey realism tattoo by Joweney Balentien"
          className="w-full h-full object-cover object-[55%_30%] scale-105"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
      </div>

      <div className="grain absolute inset-0 pointer-events-none" />

      {/* Top corner location strip */}
      <div className="absolute top-24 md:top-24 left-0 right-0 z-10 hidden xl:block">
        <div className="container-x flex items-center justify-between text-xs uppercase tracking-[0.28em] text-white/55">
          <div className="flex items-center gap-3">
            <MapPin className="w-3.5 h-3.5 text-[hsl(var(--coral))]" />
            <span>Willemstad — 12.108°N</span>
          </div>
          <div className="flex items-center gap-3">
            <span>Rotterdam — 51.924°N</span>
            <MapPin className="w-3.5 h-3.5 text-[hsl(var(--mustard))]" />
          </div>
        </div>
      </div>

      {/* Hero content */}
      <div className="relative z-10 container-x min-h-[100svh] flex flex-col justify-end pb-24 md:pb-28 pt-32">
        <div className="max-w-4xl">
          <p data-testid="hero-overline" className="overline mb-6">{t.hero.overline}</p>

          <h1
            data-testid="hero-title"
            className="font-display text-5xl sm:text-7xl lg:text-[8.5rem] leading-[0.92] tracking-tighter uppercase text-white"
          >
            {t.hero.title_1}{" "}
            <span className="font-script normal-case text-[1.4em] leading-[0.7] align-baseline inline-block -translate-y-1">
              {t.hero.title_2}
            </span>
            <br />
            <span className="text-white/55">{t.hero.title_3}</span>
          </h1>

          <p
            data-testid="hero-sub"
            className="mt-8 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed"
          >
            {t.hero.sub}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              data-testid="hero-book-btn"
              href={buildWhatsAppUrl(t.whatsapp_pretext)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-wa"
            >
              <MessageCircle className="w-4 h-4" /> {t.hero.cta_primary}
            </a>
            <a data-testid="hero-work-btn" href="#work" className="btn-ghost">
              {t.hero.cta_secondary} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.32em] text-white/40">
          {t.hero.scroll}
        </span>
        <div className="w-px h-10 bg-gradient-to-b from-white/60 to-transparent" />
      </div>
    </section>
  );
}
